import React, { useEffect, useState } from 'react'
import moment from 'moment'
import validator from 'validator'
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import BlockIcon from '@mui/icons-material/Block'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import SendIcon from '@mui/icons-material/Send'
import API from '../../../../services/API'
import { codeFont } from './ShareCodeCard'
import {
  describeExpiry,
  getAccessRecordUrl,
  getShareCodeStatus,
  personName,
  SHARE_CODE_STATUS_META,
} from './shareCodeUtils'

const Row = ({ label, value }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    spacing={2}
    py={0.75}
  >
    <Typography variant="body2" color="text.secondary" flexShrink={0}>
      {label}
    </Typography>
    {React.isValidElement(value) ? (
      value
    ) : (
      <Typography
        variant="body2"
        fontWeight={600}
        textAlign="right"
        sx={{ overflowWrap: 'anywhere' }}
      >
        {value ?? '—'}
      </Typography>
    )}
  </Stack>
)

const attemptIcon = (entry) => {
  if (typeof entry.success !== 'boolean') return <LinkOffIcon fontSize="small" />
  return entry.success ? (
    <CheckCircleIcon fontSize="small" />
  ) : (
    <BlockIcon fontSize="small" />
  )
}

const attemptTone = (entry) => {
  if (typeof entry.success !== 'boolean') return 'error'
  return entry.success ? 'success' : 'warning'
}

const attemptTitle = (entry) => {
  const who = [entry.name, entry.organisation && `(${entry.organisation})`]
    .filter(Boolean)
    .join(' ')
  if (typeof entry.success !== 'boolean') {
    return `${entry.note || 'Note'}${who ? ` by ${who}` : ''}`
  }
  if (entry.success) return `Downloaded${who ? ` by ${who}` : ''}`
  return `Refused: ${entry.failReason || 'details did not match'}`
}

const attemptSubtitle = (entry) => {
  if (typeof entry.success !== 'boolean' || entry.success) return null
  return [entry.name, entry.organisation && `(${entry.organisation})`]
    .filter(Boolean)
    .join(' ')
}

const ShareCodeDetailsDialog = ({
  shareCode,
  open,
  onClose,
  onRevoked,
  onCopy,
}) => {
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState(null)
  const [emailStatus, setEmailStatus] = useState('idle')
  const [confirmRevoke, setConfirmRevoke] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const [revokeError, setRevokeError] = useState(null)

  useEffect(() => {
    if (!open) return
    setEmail('')
    setEmailError(null)
    setEmailStatus('idle')
    setConfirmRevoke(false)
    setRevoking(false)
    setRevokeError(null)
  }, [open, shareCode?._id])

  if (!shareCode) return null

  const status = getShareCodeStatus(shareCode)
  const meta = SHARE_CODE_STATUS_META[status]
  const isActive = status === 'active'
  const accessUrl = getAccessRecordUrl()
  const attempts = [...(shareCode.accessLogs ?? [])].sort(
    (a, b) => moment(b?.date ?? 0).valueOf() - moment(a?.date ?? 0).valueOf(),
  )

  const sendEmail = async () => {
    const trimmed = email.trim()
    if (!validator.isEmail(trimmed)) {
      setEmailError('Please enter a valid email address.')
      return
    }
    setEmailError(null)
    setEmailStatus('sending')
    try {
      const res = await API.post('/epcr/email-share-code', {
        email: trimmed,
        code: shareCode.code,
      })
      setEmailStatus(res.status === 200 ? 'sent' : 'failed')
    } catch (e) {
      setEmailStatus('failed')
    }
  }

  const revoke = async () => {
    setRevoking(true)
    setRevokeError(null)
    try {
      const res = await API.post('/epcr/revoke-share-code', {
        selectedShareCode: shareCode._id,
      })
      if (res.status === 200) {
        onRevoked?.(res.data)
        setConfirmRevoke(false)
      } else if (res.status === 403) {
        setRevokeError(
          'Only the record owner, Governance or a site admin can revoke this code.',
        )
      } else {
        setRevokeError('Unable to revoke the code right now. Please try again.')
      }
    } catch (e) {
      setRevokeError('Connection lost. The code was not revoked.')
    } finally {
      setRevoking(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Typography
            component="span"
            sx={{
              ...codeFont,
              fontSize: '1.6rem',
              textDecoration: status === 'revoked' ? 'line-through' : 'none',
            }}
          >
            {shareCode.code}
          </Typography>
          <Chip
            size="small"
            label={meta.label}
            color={meta.color}
            variant={status === 'expired' ? 'outlined' : 'filled'}
            sx={{ borderRadius: 1, fontWeight: 700 }}
          />
          {isActive && (
            <IconButton
              size="small"
              aria-label="Copy share code"
              onClick={() => onCopy?.(shareCode.code, 'Share code')}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {describeExpiry(shareCode)}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Stack divider={<Divider sx={{ opacity: 0.6 }} />}>
            <Row
              label="Created by"
              value={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar
                    src={shareCode.createdBy?.photograph}
                    sx={{ width: 24, height: 24 }}
                  />
                  <Typography variant="body2" fontWeight={600}>
                    {personName(shareCode.createdBy) || 'Unknown'}
                  </Typography>
                </Stack>
              }
            />
            <Row
              label="Created"
              value={
                shareCode.createdAt
                  ? moment(shareCode.createdAt).format('HH:mm, ddd D MMM YYYY')
                  : null
              }
            />
            <Row
              label={
                status === 'expired'
                  ? 'Expired'
                  : status === 'revoked'
                    ? 'Expiry'
                    : 'Expires'
              }
              value={
                shareCode.expires
                  ? moment(shareCode.expires).format('HH:mm, ddd D MMM YYYY')
                  : null
              }
            />
            <Row
              label="Access page"
              value={
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    {accessUrl}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Copy access link"
                    onClick={() => onCopy?.(accessUrl, 'Access link')}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
              }
            />
          </Stack>

          {isActive && (
            <Stack spacing={1}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, textTransform: 'uppercase' }}
              >
                Email this code
              </Typography>
              {emailStatus === 'sent' ? (
                <Alert severity="success">Instructions emailed to {email.trim()}.</Alert>
              ) : (
                <Stack direction="row" gap={1} alignItems="flex-start">
                  <TextField
                    size="small"
                    fullWidth
                    type="email"
                    placeholder="recipient@hospital.nhs.uk"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    error={!!emailError || emailStatus === 'failed'}
                    helperText={
                      emailError ??
                      (emailStatus === 'failed'
                        ? 'The email could not be sent. Check the address and try again.'
                        : ' ')
                    }
                    autoComplete="off"
                  />
                  <Button
                    variant="outlined"
                    startIcon={<SendIcon />}
                    disabled={emailStatus === 'sending' || !email.trim()}
                    onClick={sendEmail}
                    sx={{ flexShrink: 0 }}
                  >
                    {emailStatus === 'sending' ? 'Sending…' : 'Send'}
                  </Button>
                </Stack>
              )}
            </Stack>
          )}

          <Stack spacing={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700, textTransform: 'uppercase' }}
            >
              Activity ({attempts.length})
            </Typography>
            {attempts.length === 0 ? (
              <Typography variant="body2" color="text.disabled">
                No one has used this code yet.
              </Typography>
            ) : (
              <Stack divider={<Divider sx={{ opacity: 0.6 }} />}>
                {attempts.map((entry, index) => {
                  const tone = attemptTone(entry)
                  const subtitle = attemptSubtitle(entry)
                  return (
                    <Stack
                      key={entry._id ?? index}
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                      py={1}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: `${tone}.main`,
                          bgcolor: `${tone}.light`,
                          opacity: 0.95,
                        }}
                      >
                        {attemptIcon(entry)}
                      </Box>
                      <Box flex={1} minWidth={0}>
                        <Typography variant="body2" fontWeight={600}>
                          {attemptTitle(entry)}
                        </Typography>
                        {subtitle && (
                          <Typography variant="caption" color="text.secondary">
                            {subtitle}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        flexShrink={0}
                      >
                        {entry.date
                          ? moment(entry.date).format('D MMM, HH:mm')
                          : ''}
                      </Typography>
                    </Stack>
                  )
                })}
              </Stack>
            )}
          </Stack>

          {revokeError && <Alert severity="error">{revokeError}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        {isActive &&
          (confirmRevoke ? (
            <>
              <Typography variant="body2" sx={{ mr: 'auto' }}>
                Revoke access? The code will stop working immediately.
              </Typography>
              <Button
                color="inherit"
                onClick={() => setConfirmRevoke(false)}
                disabled={revoking}
              >
                Keep it
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<LinkOffIcon />}
                onClick={revoke}
                disabled={revoking}
              >
                {revoking ? 'Revoking…' : 'Revoke access'}
              </Button>
            </>
          ) : (
            <Button
              color="error"
              variant="outlined"
              startIcon={<LinkOffIcon />}
              onClick={() => setConfirmRevoke(true)}
              sx={{ mr: 'auto' }}
            >
              Revoke access
            </Button>
          ))}
        {!confirmRevoke && (
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ShareCodeDetailsDialog
